import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePlan } from '../context/PlanContext';
import { useOrganization } from '../context/OrganizationContext';
import { useMode } from '../context/ModeContext';
import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { getWorkerIcon } from '../utils/workerIcons';
import UniversalVoiceWizard from '../components/UniversalVoiceWizard';
import { CreateOperatorModal } from './Operators';
import FileWorkerConfig from '../components/FileWorkerConfig';
import {
  EnvelopeSimple,
  CalendarCheck,
  CurrencyDollar,
  UsersFour,
  Plus,
  Lightning,
  Trash,
  ArrowLeft,
  Check,
  X,
  Play,
  CircleNotch,
  CheckCircle,
  WarningCircle,
  ChatText,
  Phone,
  Link as LinkIcon,
  ArrowRight,
  FileText,
  Microphone,
  Robot,
  Globe,
  Upload,
  FilePdf,
  FileDoc,
  FileXls,
  FileAudio,
  FileVideo,
  Gear,
  CaretRight,
  Brain,
  ChatCircle,
  ShieldCheck,
  PlugsConnected,
  MagicWand,
  Timer,
  HandPalm,
  TreeStructure,
  HourglassMedium,
  Pause,
} from '@phosphor-icons/react';
import { getAppLogo } from '../utils/usedApps';
import { connectApp, checkConnectionStatus, getSlug, openOAuthPopup } from '../utils/composioAuth';

/* ─────────────────────────────────────────────────
   TOOL ICONS — map step content to relevant service
   ───────────────────────────────────────────────── */
const TOOL_ICONS = {
  gmail:    { src: 'https://www.gstatic.com/images/branding/product/1x/gmail_2020q4_32dp.png', label: 'Gmail', slug: 'gmail' },
  outlook:  { src: getAppLogo('Outlook'), label: 'Outlook', slug: 'outlook' },
  // Neutral generic email — used when no provider has been chosen / connected upstream
  email:    { icon: EnvelopeSimple, label: 'Email', color: '#7D8896', slug: 'email' },
  sms:      { icon: ChatText, label: 'SMS', color: '#3B82F6', slug: 'sms' },
  phone:    { icon: Phone, label: 'Phone', color: '#8B5CF6', slug: 'phone' },
  calendar: { icon: CalendarCheck, label: 'Calendar', color: '#F59E0B', slug: 'calendar' },
  invoice:  { icon: CurrencyDollar, label: 'Invoicing', color: '#10B981', slug: 'invoice' },
  file:     { icon: FileText, label: 'File', color: '#7D8896', slug: 'file' },
};

/**
 * Detect the tool/icon for a step label.
 *
 * For generic email-related labels we now inherit the provider from upstream
 * context instead of forcefully defaulting to Gmail. Resolution order:
 *   1. Explicit "gmail"/"outlook" keyword in the label
 *   2. Nearest upstream step with an explicit `step.app` (Gmail / Outlook)
 *   3. Connected apps (if exactly one of Gmail / Outlook is connected)
 *   4. Neutral generic email icon
 */
const _detectTool = (label, ctx = {}) => {
  const l = (label || '').toLowerCase();
  const { steps = [], index = -1, connectedApps = null } = ctx;

  const isEmailLabel = l.includes('email') || l.includes('enquir') || l.includes('inbox') || l.includes('reply') || l.includes('gmail') || l.includes('outlook');
  if (isEmailLabel) {
    if (l.includes('gmail')) return TOOL_ICONS.gmail;
    if (l.includes('outlook')) return TOOL_ICONS.outlook;

    // Inherit from nearest upstream step with an explicit provider app.
    if (Array.isArray(steps) && steps.length > 0) {
      const upper = index >= 0 ? steps.slice(0, index) : steps;
      for (let i = upper.length - 1; i >= 0; i--) {
        const a = (upper[i]?.app || '').toLowerCase();
        if (a === 'outlook') return TOOL_ICONS.outlook;
        if (a === 'gmail')   return TOOL_ICONS.gmail;
      }
    }

    // Inherit from connected apps when exactly one email provider is connected.
    if (connectedApps && typeof connectedApps.has === 'function') {
      const hasOutlook = connectedApps.has('outlook');
      const hasGmail   = connectedApps.has('gmail');
      if (hasOutlook && !hasGmail) return TOOL_ICONS.outlook;
      if (hasGmail && !hasOutlook) return TOOL_ICONS.gmail;
    }

    // Neutral fallback — no Gmail bias.
    return TOOL_ICONS.email;
  }
  if (l.includes('sms') || l.includes('text message')) return TOOL_ICONS.sms;
  if (l.includes('phone') || l.includes('call')) return TOOL_ICONS.phone;
  if (l.includes('calendar') || l.includes('book') || l.includes('schedul')) return TOOL_ICONS.calendar;
  if (l.includes('invoice') || l.includes('payment') || l.includes('bill')) return TOOL_ICONS.invoice;
  return null;
};

const ToolIcon = ({ tool, size = 18 }) => {
  if (!tool) return null;
  if (tool.src) return <img src={tool.src} alt={tool.label} className="rounded" style={{ width: size, height: size }} />;
  const Icon = tool.icon;
  return <Icon size={size} style={{ color: tool.color }} weight="duotone" />;
};

/* ─────────────────────────────────────────────────
   WORKER TYPES
   ───────────────────────────────────────────────── */
const WORKER_TYPES = [
  {
    id: 'event',
    label: 'Custom Event Worker',
    description: 'Build a custom trigger-based workflow — emails, webhooks, schedules, and more.',
    icon: Lightning,
    color: '#00E5C0',
  },
  {
    id: 'file_analysis',
    label: 'File / Analysis Worker',
    description: 'Upload files for processing — audio, video, PDF, documents. Get scores, insights, reports.',
    icon: FileText,
    color: '#3B82F6',
    tag: null,
  },
  {
    id: 'chat',
    label: 'Chat Worker',
    description: 'Conversational assistant that interacts with users through persistent chat.',
    icon: ChatText,
    color: '#00E5C0',
    tag: null,
  },
  {
    id: 'voice',
    label: 'Voice Worker',
    description: 'Real-time voice interaction — phone calls, voice assistants, speech-to-speech.',
    icon: Microphone,
    color: '#F59E0B',
    tag: null,
  },
  {
    id: 'operator',
    label: 'Operator Worker',
    description: 'System-level execution — browse the web, use tools, manage files, run complex tasks.',
    icon: Globe,
    color: '#FF6B6B',
    tag: 'Orion',
  },
];

/* ─────────────────────────────────────────────────
   EVENT WORKER TEMPLATES
   ───────────────────────────────────────────────── */
const EVENT_TEMPLATES = [
  { id: 'answer_enquiries', label: 'Answer enquiries', description: 'Respond to new customer enquiries', icon: EnvelopeSimple, color: '#00E5C0' },
  { id: 'after_hours',      label: 'After-hours enquiry response', description: 'Respond to enquiries outside business hours and stay on top of overnight leads.', icon: CalendarCheck, color: '#3B82F6' },
  { id: 'send_invoices',    label: 'Send invoices', description: 'Generate and send invoices after jobs', icon: CurrencyDollar, color: '#F59E0B' },
  { id: 'follow_up',        label: 'Follow up leads', description: 'Follow up with prospects automatically', icon: UsersFour, color: '#00E5C0' },
  { id: 'custom',           label: 'Custom workflow', description: 'Build from scratch with your own steps', icon: Gear, color: '#7D8896' },
];

const STEP_META = {
  trigger:   { badge: 'When',     color: '#00E5C0', bg: 'rgba(0,229,192,0.08)',  icon: Lightning },
  condition: { badge: 'If',       color: '#F59E0B', bg: 'rgba(245,158,11,0.08)', icon: TreeStructure },
  action:    { badge: 'Do',       color: '#3B82F6', bg: 'rgba(59,130,246,0.08)', icon: Lightning },
  delay:     { badge: 'Wait',     color: '#8B5CF6', bg: 'rgba(139,92,246,0.08)', icon: Timer },
  approval:  { badge: 'Approve',  color: '#EF4444', bg: 'rgba(239,68,68,0.08)', icon: HandPalm },
  input:     { badge: 'Input',    color: '#00E5C0', bg: 'rgba(0,229,192,0.08)',  icon: ArrowRight },
  output:    { badge: 'Output',   color: '#10B981', bg: 'rgba(16,185,129,0.08)', icon: CheckCircle },
};

/* ─── Natural-language condition presets ─── */
const CONDITION_PRESETS = [
  { label: 'If enquiry is urgent',          yesBranch: 'Urgent — prioritise',      noBranch: 'Not urgent — continue normally' },
  { label: 'If message contains a quote request', yesBranch: 'Quote requested — prepare estimate', noBranch: 'No quote needed — reply normally' },
  { label: 'If customer already exists',    yesBranch: 'Existing customer',         noBranch: 'New customer — create record' },
  { label: 'If no reply within 24 hours',   yesBranch: 'Still waiting — follow up', noBranch: 'Replied — no action needed' },
  { label: 'If outside business hours',     yesBranch: 'After hours — queue for morning', noBranch: 'Business hours — handle now' },
  { label: 'If attachment is included',     yesBranch: 'Has attachment — process file', noBranch: 'No attachment — text only' },
];

/* Derive meaningful branch labels from condition text */
const _deriveBranchLabels = (conditionText) => {
  const t = (conditionText || '').toLowerCase();
  if (t.includes('urgent'))     return { yes: 'Urgent', no: 'Not urgent' };
  if (t.includes('quote'))      return { yes: 'Quote requested', no: 'No quote' };
  if (t.includes('exist'))      return { yes: 'Exists', no: 'Does not exist' };
  if (t.includes('no reply') || t.includes('no response'))  return { yes: 'Still waiting', no: 'Replied' };
  if (t.includes('outside') || t.includes('after hours'))   return { yes: 'After hours', no: 'Business hours' };
  if (t.includes('attach'))     return { yes: 'Has attachment', no: 'No attachment' };
  if (t.includes('contain'))    return { yes: 'Matches', no: 'Does not match' };
  if (t.includes('before'))     return { yes: 'Before deadline', no: 'After deadline' };
  return { yes: 'Yes — continue', no: 'No — skip' };
};

/* Human-readable delay label */
const _delayLabel = (config) => {
  if (!config) return 'Wait before continuing';
  if (config.type === 'business_day') return 'Wait until next business day';
  const v = config.value || 1;
  const u = config.unit || 'minutes';
  const singular = u.replace(/s$/, '');
  return v === 1 ? `Wait 1 ${singular}` : `Wait ${v} ${u}`;
};

const INITIAL_STEPS = {
  answer_enquiries: [
    { type: 'trigger', label: 'When a new enquiry is received' },
    { type: 'action',  label: 'Generate a professional reply' },
    { type: 'output',  label: 'Send the reply to the customer' },
  ],
  after_hours: [
    { type: 'trigger',   label: 'Detects an after-hours enquiry' },
    { type: 'action',    label: 'Prepares a reply using your business details' },
    { type: 'output',    label: 'Sends automatically if email is connected, otherwise saves as a draft' },
    { type: 'action',    label: 'Creates a morning summary of all overnight enquiries' },
  ],
  send_invoices: [
    { type: 'trigger', label: 'When a job is marked complete' },
    { type: 'action',  label: 'Generate the invoice' },
    { type: 'output',  label: 'Send the invoice to the customer' },
  ],
  follow_up: [
    { type: 'trigger',   label: 'When a new lead is created' },
    { type: 'condition', label: 'If no reply within 24 hours' },
    { type: 'output',    label: 'Send a follow-up message' },
  ],
  custom: [
    { type: 'trigger', label: 'When something happens' },
  ],
  // File worker defaults
  file_analysis: [
    { type: 'input',  label: 'Receive uploaded file' },
    { type: 'action', label: 'Load reference material (if provided)' },
    { type: 'action', label: 'Extract and structure content' },
    { type: 'action', label: 'Evaluate against instructions and criteria' },
    { type: 'output', label: 'Generate output and export result' },
  ],
  // Chat worker defaults
  chat: [
    { type: 'input',  label: 'Receive user message' },
    { type: 'action', label: 'Process with context and instructions' },
    { type: 'output', label: 'Return conversational response' },
  ],
  // Voice worker defaults
  voice: [
    { type: 'input',  label: 'Receive inbound voice call' },
    { type: 'action', label: 'Process speech and determine intent' },
    { type: 'output', label: 'Respond with voice output' },
  ],
  // Operator worker defaults
  operator: [
    { type: 'input',  label: 'Receive task instruction' },
    { type: 'action', label: 'Execute using browser, tools, or files' },
    { type: 'output', label: 'Return execution results' },
  ],
};

/* ─────────────────────────────────────────────────
   STEP CARD — Clickable, opens detail panel
   ───────────────────────────────────────────────── */
/* Auth provider grouping — Google apps share one OAuth */
const AUTH_PROVIDERS = {
  'Gmail': 'google', 'Google Calendar': 'google', 'Google Sheets': 'google',
  'Google Drive': 'google', 'Google Business Profile': 'google',
  'Outlook': 'microsoft',
};
const _getProvider = (app) => AUTH_PROVIDERS[app] || app;
const _getProviderLabel = (app) => {
  const p = AUTH_PROVIDERS[app];
  if (p === 'google') return 'Connect Google';
  if (p === 'microsoft') return 'Connect Microsoft';
  return `Connect ${app}`;
};

/* Track seen providers across StepCards (reset per render via parent) */
const _seenProviders = { current: new Set() };

const StepCard = ({ step, index, sectionIndex, onDelete, onSelect, isSelected, executionLog, isHighlighted, connectedApps, connectingApp, onConnectApp, onDragStart, onDragOver, onDrop, isDragTarget, allSteps }) => {
  const meta = STEP_META[step.type] || STEP_META.action;
  const MetaIcon = meta.icon;
  const execStep = executionLog?.find((e) => e.step_index === index);
  const tool = _detectTool(step.label, { steps: allSteps, index, connectedApps });
  const displayIdx = sectionIndex != null ? sectionIndex : index;

  // App connection logic
  const hasApp = !!step.app;
  const provider = hasApp ? _getProvider(step.app) : null;
  const isFirstForProvider = hasApp && !_seenProviders.current.has(provider);
  if (provider) _seenProviders.current.add(provider);

  const appSlug = hasApp ? getSlug(step.app) : null;
  const isConnected = appSlug && connectedApps?.has(appSlug);
  const isConnecting = appSlug && connectingApp === appSlug;

  const isCondition = step.type === 'condition';
  const isDelay = step.type === 'delay';
  const isApproval = step.type === 'approval';

  // Smart branch labels from condition text
  const branchLabels = isCondition ? _deriveBranchLabels(step.label) : null;

  return (
    <div data-testid={`step-card-${index}`}
      draggable
      onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; onDragStart?.(index); }}
      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; onDragOver?.(index); }}
      onDrop={(e) => { e.preventDefault(); onDrop?.(index); }}
    >
      {displayIdx > 0 && (
        <div className="flex justify-center py-1">
          <div className={`w-0.5 h-4 rounded-full transition-colors ${isDragTarget ? 'bg-[#00E5C0] h-6' : ''}`} style={!isDragTarget ? { backgroundColor: `${meta.color}25` } : undefined} />
        </div>
      )}
      <button
        onClick={() => onSelect(index)}
        data-testid={`step-select-${index}`}
        className={`w-full text-left group relative border rounded-xl px-5 py-3.5 transition-all duration-200 ${
          isHighlighted
            ? 'border-[rgba(0,229,192,0.6)] ring-2 ring-[rgba(0,229,192,0.25)] bg-[rgba(0,229,192,0.04)] animate-pulse'
            : isSelected
            ? 'ring-1 bg-[rgba(255,255,255,0.02)]'
            : 'hover:bg-[#1E2836]'
        } ${isCondition ? 'bg-[rgba(245,158,11,0.02)]' : isDelay ? 'bg-[rgba(139,92,246,0.02)]' : isApproval ? 'bg-[rgba(239,68,68,0.02)]' : 'bg-[#1A2332]'}`}
        style={{
          borderColor: isHighlighted ? undefined : isSelected ? `${meta.color}60` : `${meta.color}25`,
          borderLeftWidth: '3px',
          borderLeftColor: isHighlighted ? undefined : `${meta.color}80`,
        }}
      >
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center gap-2">
            <div className="w-5.5 h-5.5 rounded-md flex items-center justify-center shrink-0" style={{ backgroundColor: meta.bg, width: 22, height: 22 }}>
              <MetaIcon size={12} style={{ color: meta.color }} weight="duotone" />
            </div>
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider" style={{ background: meta.bg, color: meta.color }}>
              {meta.badge}
            </span>
            {hasApp ? (
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)]">
                <img src={getAppLogo(step.app)} alt={step.app} className="w-3.5 h-3.5 rounded object-contain"
                  onError={(e) => { e.target.style.display = 'none'; }} />
                <span className="text-[10px] text-[#B0B8C4] font-medium">{step.app}</span>
              </span>
            ) : tool && (
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)]">
                <ToolIcon tool={tool} size={14} />
                <span className="text-[10px] text-[#B0B8C4] font-medium">{tool.label}</span>
              </span>
            )}
            {execStep && (
              <span className={`inline-flex items-center gap-1 text-[10px] ${execStep.status === 'completed' ? 'text-[#00E5C0]' : execStep.status === 'skipped' ? 'text-[#F59E0B]' : 'text-[#FF6B6B]'}`}>
                {execStep.status === 'completed' ? <CheckCircle size={12} weight="fill" /> : <WarningCircle size={12} />}
                {execStep.status}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <span className="p-1 rounded cursor-grab active:cursor-grabbing text-[#5C6778] hover:text-[#7D8896] transition-colors" title="Drag to reorder">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor"><circle cx="3.5" cy="2" r="1"/><circle cx="8.5" cy="2" r="1"/><circle cx="3.5" cy="6" r="1"/><circle cx="8.5" cy="6" r="1"/><circle cx="3.5" cy="10" r="1"/><circle cx="8.5" cy="10" r="1"/></svg>
            </span>
            <span onClick={(e) => { e.stopPropagation(); onDelete(index); }} data-testid={`delete-step-${index}`} className="p-1 rounded hover:bg-[rgba(255,107,107,0.1)] text-[#7D8896] hover:text-[#FF6B6B] transition-colors cursor-pointer"><Trash size={14} /></span>
          </div>
        </div>
        <p className="text-[13px] text-[#F0F4F8] leading-relaxed pl-7">{step.label}</p>

        {/* Delay — human-readable duration inline */}
        {isDelay && (
          <div className="mt-1.5 pl-7 flex items-center gap-1.5">
            <HourglassMedium size={11} className="text-[#8B5CF6]" />
            <span className="text-[11px] text-[#A78BFA] font-medium">{_delayLabel(step.delayConfig)}</span>
          </div>
        )}

        {/* Approval — show what's being reviewed and what happens after */}
        {isApproval && (
          <div className="mt-1.5 pl-7 flex items-center gap-1.5">
            <Pause size={11} className="text-[#F87171]" />
            <span className="text-[11px] text-[#F87171] font-medium">{step.approvalNote ? `Review: ${step.approvalNote}` : 'Pauses for manual review before continuing'}</span>
          </div>
        )}

        {execStep?.output && <p className="text-xs text-[#7D8896] mt-1.5 pl-7 italic">{execStep.output}</p>}

        {/* Inline connect button */}
        {hasApp && isFirstForProvider && (
          isConnected ? (
            <div className="mt-2.5 pt-2 border-t border-[rgba(255,255,255,0.04)] pl-7">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold border border-[rgba(0,229,192,0.3)] bg-[rgba(0,229,192,0.08)] text-[#00E5C0]"
                data-testid={`builder-connected-${step.app.toLowerCase().replace(/\s+/g, '-')}-badge`}>
                <CheckCircle size={12} weight="fill" /> Connected
              </span>
            </div>
          ) : (
            <div className="mt-2.5 pt-2 border-t border-[rgba(255,255,255,0.04)] pl-7"
              onClick={(e) => { e.stopPropagation(); onConnectApp?.(step.app); }}>
              <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold border border-[rgba(0,229,192,0.25)] bg-[rgba(0,229,192,0.06)] text-[#00E5C0] hover:bg-[rgba(0,229,192,0.12)] hover:border-[rgba(0,229,192,0.4)] transition-all cursor-pointer ${isConnecting ? 'opacity-50' : ''}`}
                data-testid={`builder-connect-${step.app.toLowerCase().replace(/\s+/g, '-')}-btn`}>
                {isConnecting ? <CircleNotch size={12} className="animate-spin" /> : <PlugsConnected size={12} />}
                {isConnecting ? 'Connecting...' : `Connect ${step.app} to enable automatic ${(step.label || '').toLowerCase().includes('send') ? 'sending' : (step.label || '').toLowerCase().includes('read') ? 'reading' : 'actions'}`}
              </span>
            </div>
          )
        )}

        {isSelected && <div className="absolute -right-2 top-1/2 -translate-y-1/2 w-1 h-8 rounded-full" style={{ backgroundColor: meta.color }} />}
      </button>

      {/* Condition — visual IF / ELSE branch with smart labels */}
      {isCondition && (
        <div className="mt-2 ml-7 mr-4" data-testid={`condition-branches-${index}`}>
          <div className="flex items-center gap-2 mb-1.5">
            <div className="flex-1 h-px bg-[rgba(0,229,192,0.15)]" />
            <TreeStructure size={10} className="text-[#F59E0B]" />
            <div className="flex-1 h-px bg-[rgba(255,255,255,0.06)]" />
          </div>
          <div className="flex items-stretch gap-2.5">
            <div className="flex-1 px-3 py-2 rounded-lg border border-[rgba(0,229,192,0.2)] bg-[rgba(0,229,192,0.03)]">
              <div className="flex items-center gap-1.5 mb-0.5">
                <CheckCircle size={10} weight="fill" className="text-[#00E5C0]" />
                <span className="text-[9px] font-bold text-[#00E5C0] uppercase tracking-wider">{step.yesBranch || branchLabels?.yes || 'Yes'}</span>
              </div>
              <p className="text-[10px] text-[#7D8896]">Continue to next step</p>
            </div>
            <div className="flex-1 px-3 py-2 rounded-lg border border-[rgba(255,255,255,0.06)] bg-[rgba(255,255,255,0.015)]">
              <div className="flex items-center gap-1.5 mb-0.5">
                <X size={10} weight="bold" className="text-[#5C6778]" />
                <span className="text-[9px] font-bold text-[#5C6778] uppercase tracking-wider">{step.noBranch || branchLabels?.no || 'No'}</span>
              </div>
              <p className="text-[10px] text-[#4A5568]">Skip to end</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────
   STEP DETAIL PANEL
   ───────────────────────────────────────────────── */
const TRIGGER_OPTIONS = {
  email: ['Email received', 'Email sent', 'Email replied to'],
  calendar: ['Event created', 'Event updated', 'Event starting soon'],
  sms: ['SMS received', 'SMS sent'],
  phone: ['Call received', 'Call ended'],
  invoice: ['Invoice created', 'Payment received'],
  file: ['File uploaded', 'File processed'],
  default: ['Event triggered'],
};

const ACTION_OPTIONS = {
  email: ['Send email', 'Reply to email', 'Forward email', 'Draft email'],
  sms: ['Send SMS', 'Send bulk SMS'],
  phone: ['Make call', 'Send voicemail'],
  calendar: ['Create event', 'Update event', 'Cancel event'],
  invoice: ['Generate invoice', 'Send invoice', 'Mark as paid'],
  file: ['Extract content', 'Analyze document', 'Generate report'],
  default: ['Run action', 'Send notification', 'Update record'],
};

/* ─────────────────────────────────────────────────
   INPUT SOURCES & OUTPUT DESTINATIONS (per worker type)
   ───────────────────────────────────────────────── */
const INPUT_SOURCES = {
  file_analysis: [
    { id: 'upload', label: 'File upload', desc: 'Manual upload through the UI' },
    { id: 'email', label: 'Email attachment', desc: 'Automatically process email attachments' },
    { id: 'webhook', label: 'Webhook payload', desc: 'Receive files via HTTP webhook' },
    { id: 'watch', label: 'Watch folder', desc: 'Monitor a cloud folder for new files' },
  ],
  chat: [
    { id: 'web_chat', label: 'Web chat widget', desc: 'Embeddable chat for your website' },
    { id: 'api', label: 'API endpoint', desc: 'Integrate via REST API' },
    { id: 'sms', label: 'SMS', desc: 'Chat via text messages' },
  ],
  voice: [
    { id: 'inbound', label: 'Inbound call', desc: 'Handle incoming phone calls' },
    { id: 'outbound', label: 'Outbound call', desc: 'Initiate calls to contacts' },
    { id: 'voice_api', label: 'Voice API', desc: 'Programmatic voice interaction' },
  ],
  operator: [
    { id: 'manual', label: 'Manual task', desc: 'Triggered by a user instruction' },
    { id: 'scheduled', label: 'Scheduled', desc: 'Runs on a recurring schedule' },
    { id: 'api', label: 'API trigger', desc: 'Triggered via API call' },
  ],
};

const OUTPUT_DESTINATIONS = {
  file_analysis: [
    { id: 'activity', label: 'Save to Activity', desc: 'Store results in the activity feed' },
    { id: 'email_report', label: 'Email report', desc: 'Send results via email' },
    { id: 'webhook', label: 'Webhook', desc: 'POST results to an endpoint' },
    { id: 'export', label: 'Export file', desc: 'Generate CSV, PDF, or JSON export' },
  ],
  chat: [
    { id: 'chat_response', label: 'Chat response', desc: 'Reply in the conversation' },
    { id: 'activity', label: 'Activity log', desc: 'Log conversation to activity' },
    { id: 'email_summary', label: 'Email summary', desc: 'Send conversation summary' },
  ],
  voice: [
    { id: 'voice_response', label: 'Voice response', desc: 'Speak back to the caller' },
    { id: 'transcript', label: 'Call transcript', desc: 'Save transcription to activity' },
    { id: 'activity', label: 'Activity log', desc: 'Log call details' },
  ],
  operator: [
    { id: 'result', label: 'Task result', desc: 'Return execution results' },
    { id: 'activity', label: 'Activity log', desc: 'Log task execution' },
    { id: 'webhook', label: 'Webhook', desc: 'POST results to an endpoint' },
  ],
  default: [
    { id: 'activity', label: 'Save to Activity', desc: 'Store in the activity feed' },
    { id: 'email', label: 'Send notification', desc: 'Notify via email' },
  ],
};

const StepDetailPanel = ({ step, index, workerType, onUpdate, onClose, connectedApps, connectingApp, onConnectApp, allSteps }) => {
  const navigate = useNavigate();
  const meta = STEP_META[step.type] || STEP_META.action;
  const MetaIcon = meta.icon;
  const tool = _detectTool(step.label, { steps: allSteps, index, connectedApps });
  const hasApp = !!step.app;
  const toolSlug = tool?.slug || 'default';
  const options = step.type === 'trigger' ? (TRIGGER_OPTIONS[toolSlug] || TRIGGER_OPTIONS.default) : (ACTION_OPTIONS[toolSlug] || ACTION_OPTIONS.default);

  const appSlug = hasApp ? getSlug(step.app) : null;
  const isConnected = appSlug && connectedApps?.has(appSlug);
  const isConnecting = appSlug && connectingApp === appSlug;

  const [label, setLabel] = useState(step.label);
  const [instructions, setInstructions] = useState(step.instructions || '');
  const [selectedEvent, setSelectedEvent] = useState(step.event || options[0]);
  const [filters, setFilters] = useState(step.filters || []);

  // Delay config state
  const [delayType, setDelayType] = useState(step.delayConfig?.type || 'duration');
  const [delayValue, setDelayValue] = useState(step.delayConfig?.value || 30);
  const [delayUnit, setDelayUnit] = useState(step.delayConfig?.unit || 'minutes');

  // Condition config state
  const [yesBranch, setYesBranch] = useState(step.yesBranch || 'Continue to next step');
  const [noBranch, setNoBranch] = useState(step.noBranch || 'Skip to end');

  // Approval config state
  const [approvalNote, setApprovalNote] = useState(step.approvalNote || '');

  const handleSave = () => {
    const updated = { ...step, label, instructions, event: selectedEvent, filters };
    if (step.type === 'delay') {
      updated.delayConfig = { type: delayType, value: delayValue, unit: delayUnit };
    }
    if (step.type === 'condition') {
      updated.yesBranch = yesBranch;
      updated.noBranch = noBranch;
    }
    if (step.type === 'approval') {
      updated.approvalNote = approvalNote;
    }
    onUpdate(index, updated);
    onClose();
  };
  const addFilter = () => setFilters(prev => [...prev, { field: '', operator: 'equals', value: '' }]);
  const updateFilter = (idx, key, val) => setFilters(prev => prev.map((f, i) => i === idx ? { ...f, [key]: val } : f));
  const removeFilter = (idx) => setFilters(prev => prev.filter((_, i) => i !== idx));

  return (
    <div className="bg-[#1E2836] border border-[rgba(255,255,255,0.08)] rounded-xl overflow-hidden" data-testid="step-detail-panel">
      <div className="px-5 py-4 border-b border-[rgba(255,255,255,0.08)] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: meta.bg, border: `1px solid ${meta.color}30` }}>
            <MetaIcon size={15} style={{ color: meta.color }} weight="duotone" />
          </div>
          {hasApp ? (
            <img src={getAppLogo(step.app)} alt={step.app} className="w-5 h-5 rounded object-contain"
              onError={(e) => { e.target.style.display = 'none'; }} />
          ) : tool && <ToolIcon tool={tool} size={20} />}
          <span className="text-sm font-semibold text-[#F0F4F8]">
            {step.type === 'delay' ? 'Delay configuration' : step.type === 'approval' ? 'Approval gate' : step.type === 'condition' ? 'Condition logic' : `${step.app || tool?.label || 'Step'} configuration`}
          </span>
          <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wider" style={{ background: meta.bg, color: meta.color }}>{meta.badge}</span>
        </div>
        <button onClick={onClose} data-testid="close-step-detail" className="p-1.5 rounded-lg hover:bg-[rgba(255,255,255,0.06)] text-[#7D8896] hover:text-[#F0F4F8] transition-colors"><X size={16} /></button>
      </div>
      <div className="px-5 py-4 space-y-5 max-h-[60vh] overflow-y-auto">

        {/* ── DELAY STEP CONFIG ── */}
        {step.type === 'delay' && (
          <div data-testid="delay-config-section">
            <p className="text-[11px] font-semibold text-[#7D8896] uppercase tracking-wider mb-3">How long should the worker wait?</p>
            <div className="space-y-2">
              <button onClick={() => { setDelayType('duration'); setLabel(_delayLabel({ type: 'duration', value: delayValue, unit: delayUnit })); }} data-testid="delay-type-duration"
                className={`w-full text-left px-4 py-3 rounded-xl border transition-all ${delayType === 'duration' ? 'border-[rgba(139,92,246,0.35)] bg-[rgba(139,92,246,0.06)]' : 'border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.15)]'}`}>
                <div className="flex items-center gap-2 mb-1.5">
                  <Timer size={14} className="text-[#8B5CF6]" />
                  <span className={`text-sm font-medium ${delayType === 'duration' ? 'text-[#F0F4F8]' : 'text-[#B0B8C4]'}`}>Wait for a specific time</span>
                </div>
                {delayType === 'duration' && (
                  <div className="flex items-center gap-2 mt-2 ml-6">
                    <span className="text-[11px] text-[#7D8896]">Wait</span>
                    <input type="number" min={1} value={delayValue} onChange={(e) => { const v = parseInt(e.target.value) || 1; setDelayValue(v); setLabel(_delayLabel({ type: 'duration', value: v, unit: delayUnit })); }} data-testid="delay-value-input"
                      className="w-16 px-2.5 py-1.5 bg-[#1F2937] border border-[rgba(255,255,255,0.12)] rounded-lg text-sm text-[#F0F4F8] text-center focus:outline-none focus:border-[rgba(139,92,246,0.4)]" />
                    <select value={delayUnit} onChange={(e) => { setDelayUnit(e.target.value); setLabel(_delayLabel({ type: 'duration', value: delayValue, unit: e.target.value })); }} data-testid="delay-unit-select"
                      className="px-2.5 py-1.5 bg-[#1F2937] border border-[rgba(255,255,255,0.12)] rounded-lg text-sm text-[#F0F4F8] appearance-none focus:outline-none">
                      <option value="minutes">minutes</option>
                      <option value="hours">hours</option>
                      <option value="days">days</option>
                    </select>
                  </div>
                )}
              </button>
              <button onClick={() => { setDelayType('business_day'); setLabel('Wait until next business day'); }} data-testid="delay-type-business-day"
                className={`w-full text-left px-4 py-3 rounded-xl border transition-all ${delayType === 'business_day' ? 'border-[rgba(139,92,246,0.35)] bg-[rgba(139,92,246,0.06)]' : 'border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.15)]'}`}>
                <div className="flex items-center gap-2">
                  <HourglassMedium size={14} className="text-[#8B5CF6]" />
                  <span className={`text-sm font-medium ${delayType === 'business_day' ? 'text-[#F0F4F8]' : 'text-[#B0B8C4]'}`}>Wait until next business day</span>
                </div>
                <p className="text-[11px] text-[#5C6778] mt-1 ml-6">Resumes at 9:00 AM on the next weekday</p>
              </button>
            </div>
          </div>
        )}

        {/* ── APPROVAL STEP CONFIG ── */}
        {step.type === 'approval' && (
          <div data-testid="approval-config-section">
            <div className="px-4 py-3.5 rounded-xl bg-[rgba(239,68,68,0.04)] border border-[rgba(239,68,68,0.12)] mb-4">
              <div className="flex items-center gap-2 mb-1.5">
                <HandPalm size={14} className="text-[#EF4444]" />
                <span className="text-sm font-medium text-[#F0F4F8]">This step pauses the workflow</span>
              </div>
              <p className="text-[11px] text-[#7D8896] leading-relaxed">The worker will stop here and wait for your approval before continuing. Use this to review drafts, verify data, or approve sensitive actions.</p>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-[#7D8896] uppercase tracking-wider mb-1.5">What needs to be reviewed?</p>
              <p className="text-[10px] text-[#5C6778] mb-2">This note will be shown when approval is requested</p>
              <textarea value={approvalNote} onChange={(e) => { setApprovalNote(e.target.value); if (e.target.value.trim()) setLabel(`Review: ${e.target.value.trim()}`); }} rows={2} data-testid="approval-note-input"
                className="w-full px-4 py-3 bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-xl text-sm text-[#F0F4F8] placeholder-[#5C6778] focus:outline-none focus:border-[rgba(239,68,68,0.3)] transition-colors resize-none"
                placeholder="e.g. Review the draft reply before sending to client" />
            </div>
          </div>
        )}

        {/* ── CONDITION STEP CONFIG ── */}
        {step.type === 'condition' && (
          <div data-testid="condition-config-section">
            <p className="text-[11px] font-semibold text-[#7D8896] uppercase tracking-wider mb-2">Choose a condition</p>
            <p className="text-[10px] text-[#5C6778] mb-3">Pick a common condition or write your own in plain language</p>
            <div className="space-y-1.5 mb-4">
              {CONDITION_PRESETS.map((preset) => (
                <button key={preset.label} onClick={() => { setLabel(preset.label); setYesBranch(preset.yesBranch); setNoBranch(preset.noBranch); }}
                  data-testid={`condition-preset-${preset.label.replace(/\s+/g, '-').toLowerCase()}`}
                  className={`w-full text-left px-3.5 py-2.5 rounded-lg border transition-all ${
                    label === preset.label ? 'border-[rgba(245,158,11,0.35)] bg-[rgba(245,158,11,0.06)]' : 'border-[rgba(255,255,255,0.06)] hover:border-[rgba(245,158,11,0.2)] bg-[rgba(255,255,255,0.015)]'
                  }`}>
                  <div className="flex items-center gap-2">
                    <TreeStructure size={12} className="text-[#F59E0B] shrink-0" />
                    <span className={`text-[13px] ${label === preset.label ? 'text-[#F0F4F8] font-medium' : 'text-[#B0B8C4]'}`}>{preset.label}</span>
                  </div>
                </button>
              ))}
            </div>
            <div className="mb-4">
              <p className="text-[11px] font-semibold text-[#7D8896] uppercase tracking-wider mb-1.5">Or describe your own condition</p>
              <input value={label} onChange={(e) => { setLabel(e.target.value); const derived = _deriveBranchLabels(e.target.value); setYesBranch(derived.yes); setNoBranch(derived.no); }} data-testid="condition-custom-input"
                className="w-full px-4 py-2.5 bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-xl text-sm text-[#F0F4F8] placeholder-[#5C6778] focus:outline-none focus:border-[rgba(245,158,11,0.35)]"
                placeholder='e.g. "If the customer has previously purchased"' />
            </div>
            <p className="text-[11px] font-semibold text-[#7D8896] uppercase tracking-wider mb-2">Branch labels</p>
            <div className="flex gap-2.5">
              <div className="flex-1 px-3 py-2.5 rounded-lg border border-[rgba(0,229,192,0.15)] bg-[rgba(0,229,192,0.03)]">
                <div className="flex items-center gap-1.5 mb-1">
                  <CheckCircle size={10} weight="fill" className="text-[#00E5C0]" />
                  <span className="text-[9px] font-bold text-[#00E5C0] uppercase tracking-wider">True path</span>
                </div>
                <input value={yesBranch} onChange={(e) => setYesBranch(e.target.value)} data-testid="condition-yes-input"
                  className="w-full px-2.5 py-1.5 bg-[#1F2937] border border-[rgba(255,255,255,0.06)] rounded text-[12px] text-[#F0F4F8] placeholder-[#5C6778] focus:outline-none"
                  placeholder="e.g. Urgent" />
              </div>
              <div className="flex-1 px-3 py-2.5 rounded-lg border border-[rgba(255,255,255,0.06)] bg-[rgba(255,255,255,0.015)]">
                <div className="flex items-center gap-1.5 mb-1">
                  <X size={10} weight="bold" className="text-[#5C6778]" />
                  <span className="text-[9px] font-bold text-[#5C6778] uppercase tracking-wider">False path</span>
                </div>
                <input value={noBranch} onChange={(e) => setNoBranch(e.target.value)} data-testid="condition-no-input"
                  className="w-full px-2.5 py-1.5 bg-[#1F2937] border border-[rgba(255,255,255,0.06)] rounded text-[12px] text-[#F0F4F8] placeholder-[#5C6778] focus:outline-none"
                  placeholder="e.g. Not urgent" />
              </div>
            </div>
          </div>
        )}

        {/* Connection section — show for app steps or tool-detected steps */}
        {(hasApp || tool) && step.type !== 'delay' && step.type !== 'approval' && (
          <div data-testid="step-connection-section">
            <p className="text-[11px] font-semibold text-[#7D8896] uppercase tracking-wider mb-2">Connection</p>
            <div className="flex items-center justify-between px-4 py-3 rounded-xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.08)]">
              <div className="flex items-center gap-3">
                {hasApp ? (
                  <img src={getAppLogo(step.app)} alt={step.app} className="w-6 h-6 rounded object-contain"
                    onError={(e) => { e.target.style.display = 'none'; }} />
                ) : <ToolIcon tool={tool} size={24} />}
                <span className="text-sm text-[#F0F4F8] font-medium">{step.app || tool.label}</span>
              </div>
              <button
                onClick={() => {
                  if (isConnected) return;
                  if (hasApp && onConnectApp) {
                    onConnectApp(step.app);
                  } else {
                    navigate('/apps?connect=' + (step.app || tool.slug));
                  }
                }}
                disabled={isConnecting || isConnected}
                data-testid={`step-connect-${(step.app || toolSlug).toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  isConnected
                    ? 'text-[#00E5C0] bg-[rgba(0,229,192,0.08)] border border-[rgba(0,229,192,0.2)]'
                    : 'text-[#00E5C0] bg-[rgba(0,229,192,0.08)] border border-[rgba(0,229,192,0.15)] hover:bg-[rgba(0,229,192,0.15)]'
                } disabled:opacity-50`}
              >
                {isConnected ? 'Connected' : isConnecting ? 'Connecting...' : 'Connect'}
              </button>
            </div>
          </div>
        )}
        {/* Action to Perform — not for delay/approval */}
        {step.type !== 'delay' && step.type !== 'approval' && (
          <div data-testid="step-event-section">
            <p className="text-[11px] font-semibold text-[#7D8896] uppercase tracking-wider mb-2">
              {step.type === 'trigger' ? 'Trigger a run when' : step.type === 'input' ? 'Input type' : step.type === 'output' ? 'Output type' : step.type === 'condition' ? 'Condition' : 'Action to perform'}<span className="text-[#FF6B6B] ml-0.5">*</span>
            </p>
            <div className="relative">
              <select value={selectedEvent} onChange={(e) => setSelectedEvent(e.target.value)} data-testid="step-event-select"
                className="w-full px-4 py-3 pr-10 bg-[#1F2937] border border-[rgba(255,255,255,0.12)] rounded-xl text-sm text-[#F0F4F8] focus:outline-none focus:border-[rgba(0,229,192,0.4)] hover:border-[rgba(255,255,255,0.25)] transition-colors appearance-none cursor-pointer">
                {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
              <CaretRight size={14} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#7D8896] rotate-90 pointer-events-none" />
            </div>
          </div>
        )}
        {/* Description */}
        <div data-testid="step-description-section">
          <p className="text-[11px] font-semibold text-[#7D8896] uppercase tracking-wider mb-2">Description</p>
          <textarea value={label} onChange={(e) => setLabel(e.target.value)} rows={2} data-testid="step-description-input"
            className="w-full px-4 py-3 bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-xl text-sm text-[#F0F4F8] placeholder-[#5C6778] focus:outline-none focus:border-[rgba(0,229,192,0.4)] transition-colors resize-none" placeholder="Describe what this step does..." />
        </div>
        {/* Instructions — manual edit for step-specific rules (not for delay/approval) */}
        {step.type !== 'delay' && step.type !== 'approval' && (
          <div data-testid="step-instructions-section">
            <p className="text-[11px] font-semibold text-[#7D8896] uppercase tracking-wider mb-1">Instructions</p>
            <p className="text-[10px] text-[#5C6778] mb-2">Specific rules for how this step should behave. You can also set these via chat.</p>
            <textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} rows={3} data-testid="step-instructions-input"
              className="w-full px-4 py-3 bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-xl text-sm text-[#F0F4F8] placeholder-[#5C6778] focus:outline-none focus:border-[rgba(0,229,192,0.4)] transition-colors resize-none"
              placeholder="e.g. Only upload customer name, exclude phone number..." />
          </div>
        )}
        {/* Optional Filters — not for delay/approval */}
        {step.type !== 'delay' && step.type !== 'approval' && (
          <div data-testid="step-filters-section">
            <p className="text-[11px] font-semibold text-[#7D8896] uppercase tracking-wider mb-1">Optional filters</p>
            <p className="text-xs text-[#5C6778] mb-3">Only process when these conditions are met.</p>
            {filters.map((f, i) => (
              <div key={i} className="flex items-center gap-2 mb-2" data-testid={`filter-row-${i}`}>
                <input value={f.field} onChange={(e) => updateFilter(i, 'field', e.target.value)} placeholder="Field" className="flex-1 px-3 py-2 bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-lg text-xs text-[#F0F4F8] focus:outline-none" />
                <select value={f.operator} onChange={(e) => updateFilter(i, 'operator', e.target.value)} className="px-2 py-2 bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-lg text-xs text-[#F0F4F8] appearance-none">
                  <option value="equals">equals</option><option value="contains">contains</option><option value="not_equals">not equals</option><option value="starts_with">starts with</option>
                </select>
                <input value={f.value} onChange={(e) => updateFilter(i, 'value', e.target.value)} placeholder="Value" className="flex-1 px-3 py-2 bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-lg text-xs text-[#F0F4F8] focus:outline-none" />
                <button onClick={() => removeFilter(i)} className="p-1.5 rounded-lg hover:bg-[rgba(255,107,107,0.1)] text-[#7D8896] hover:text-[#FF6B6B] transition-colors"><X size={12} /></button>
              </div>
            ))}
            <button onClick={addFilter} data-testid="add-filter-btn" className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-[#00E5C0] bg-[rgba(0,229,192,0.06)] border border-[rgba(0,229,192,0.12)] hover:bg-[rgba(0,229,192,0.1)] transition-colors">
              <Plus size={12} weight="bold" /> Add filter
            </button>
          </div>
        )}
        {/* Worker-type-specific: Input Source */}
        {workerType && step.type === 'input' && workerType !== 'event' && INPUT_SOURCES[workerType] && (
          <div data-testid="step-input-source-section">
            <p className="text-[11px] font-semibold text-[#7D8896] uppercase tracking-wider mb-2">Input source</p>
            <div className="space-y-1.5">
              {INPUT_SOURCES[workerType].map(src => (
                <button key={src.id} onClick={() => onUpdate(index, { ...step, inputSource: src.id })} data-testid={`input-source-${src.id}`}
                  className={`w-full text-left px-4 py-2.5 rounded-xl border transition-all ${
                    step.inputSource === src.id
                      ? 'border-[rgba(139,92,246,0.3)] bg-[rgba(139,92,246,0.04)]'
                      : 'border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.12)]'
                  }`}>
                  <p className={`text-sm ${step.inputSource === src.id ? 'text-[#F0F4F8]' : 'text-[#B0B8C4]'}`}>{src.label}</p>
                  <p className="text-[10px] text-[#5C6778] mt-0.5">{src.desc}</p>
                </button>
              ))}
            </div>
          </div>
        )}
        {/* Worker-type-specific: Output Destination */}
        {workerType && step.type === 'output' && (OUTPUT_DESTINATIONS[workerType] || OUTPUT_DESTINATIONS.default) && (
          <div data-testid="step-output-dest-section">
            <p className="text-[11px] font-semibold text-[#7D8896] uppercase tracking-wider mb-2">Destination</p>
            <div className="space-y-1.5">
              {(OUTPUT_DESTINATIONS[workerType] || OUTPUT_DESTINATIONS.default).map(dest => (
                <button key={dest.id} onClick={() => onUpdate(index, { ...step, outputDest: dest.id })} data-testid={`output-dest-${dest.id}`}
                  className={`w-full text-left px-4 py-2.5 rounded-xl border transition-all ${
                    step.outputDest === dest.id
                      ? 'border-[rgba(16,185,129,0.3)] bg-[rgba(16,185,129,0.04)]'
                      : 'border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.12)]'
                  }`}>
                  <p className={`text-sm ${step.outputDest === dest.id ? 'text-[#F0F4F8]' : 'text-[#B0B8C4]'}`}>{dest.label}</p>
                  <p className="text-[10px] text-[#5C6778] mt-0.5">{dest.desc}</p>
                </button>
              ))}
            </div>
          </div>
        )}
        {step.type !== 'delay' && step.type !== 'approval' && step.type !== 'condition' && (
          <div data-testid="step-output-section">
            <p className="text-[11px] font-semibold text-[#7D8896] uppercase tracking-wider mb-2">Step output</p>
            <div className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.08)]">
              {tool && <ToolIcon tool={tool} size={16} />}
              <span className="text-xs text-[#B0B8C4]">{tool?.label || 'Step'} output</span>
            </div>
          </div>
        )}
      </div>
      <div className="px-5 py-4 border-t border-[rgba(255,255,255,0.08)] flex items-center justify-end gap-3">
        <button onClick={onClose} className="px-4 py-2 rounded-lg text-xs text-[#7D8896] border border-[rgba(255,255,255,0.08)] hover:text-[#B0B8C4] transition-colors">Cancel</button>
        <button onClick={handleSave} data-testid="step-detail-done" className="px-5 py-2 bg-[#00E5C0] text-[#12161C] text-xs font-medium rounded-lg hover:bg-[#00CCB0] transition-colors">Done</button>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────
   ADD STEP MENU
   ───────────────────────────────────────────────── */
const AddStepMenu = ({ onAdd, workerType }) => {
  const [open, setOpen] = useState(false);
  // Unified capabilities — all step types available regardless of worker type
  const hasEventTrigger = workerType === 'event';
  const stepTypes = [
    { type: hasEventTrigger ? 'trigger' : 'input', label: hasEventTrigger ? 'Add trigger' : 'Add input', icon: hasEventTrigger ? Lightning : ArrowRight, color: '#00E5C0' },
    { type: 'action',    label: 'Add action',      icon: Lightning,     color: '#3B82F6' },
    { type: 'condition', label: 'Add condition',    icon: TreeStructure, color: '#F59E0B' },
    { type: 'delay',     label: 'Add delay',        icon: Timer,         color: '#8B5CF6' },
    { type: 'approval',  label: 'Add approval',     icon: HandPalm,      color: '#EF4444' },
    { type: 'output',    label: 'Add output',       icon: CheckCircle,   color: '#10B981' },
  ];

  return (
    <div className="mt-4" data-testid="add-step-menu">
      {!open ? (
        <button onClick={() => setOpen(true)} data-testid="add-step-trigger"
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-[rgba(255,255,255,0.08)] text-[#7D8896] hover:text-[#00E5C0] hover:border-[rgba(0,229,192,0.2)] transition-colors text-sm">
          <Plus size={14} weight="bold" /> Add step
        </button>
      ) : (
        <div className="grid grid-cols-2 gap-2 p-3 rounded-xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.08)]">
          {stepTypes.map(({ type, label, icon: Icon, color }) => (
            <button key={type} data-testid={`add-${type}-step`}
              onClick={() => {
                const defaultLabels = {
                  delay: 'Wait 30 minutes',
                  approval: 'Wait for approval before continuing',
                  condition: 'If enquiry is urgent',
                };
                const defaultConfig = {};
                if (type === 'condition') {
                  defaultConfig.yesBranch = 'Urgent — prioritise';
                  defaultConfig.noBranch = 'Not urgent — continue normally';
                }
                if (type === 'delay') {
                  defaultConfig.delayConfig = { type: 'duration', value: 30, unit: 'minutes' };
                }
                onAdd({ type, label: defaultLabels[type] || `New ${type} step`, ...defaultConfig });
                setOpen(false);
              }}
              className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left hover:bg-[rgba(255,255,255,0.04)] transition-colors text-xs text-[#B0B8C4] hover:text-[#F0F4F8]">
              <div className="w-6 h-6 rounded-md flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}15` }}>
                <Icon size={13} style={{ color }} weight="duotone" />
              </div>
              {label}
            </button>
          ))}
          <button onClick={() => setOpen(false)} className="col-span-2 mt-1 px-3 py-1.5 rounded-lg text-xs text-[#7D8896] hover:text-[#B0B8C4] transition-colors text-center">Cancel</button>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────
   CONNECTIONS PANEL
   ───────────────────────────────────────────────── */
const ConnectionsPanel = ({ steps, connectedApps, connectingApp, onConnectApp }) => {
  // Derive needed connections from step.app (primary) or label detection (fallback)
  const appEntries = [];
  const seen = new Set();
  for (const s of steps) {
    if (s.app) {
      const slug = getSlug(s.app);
      if (slug && !seen.has(slug)) {
        seen.add(slug);
        const action = (s.label || '').toLowerCase();
        const verb = action.includes('send') ? 'sending' : action.includes('read') ? 'reading' : action.includes('create') ? 'creating' : action.includes('reply') ? 'replying' : 'automatic actions';
        appEntries.push({ label: s.app, slug, logo: getAppLogo(s.app), verb });
      }
    } else {
      const tool = _detectTool(s.label, { steps, index: steps.indexOf(s), connectedApps });
      if (tool && !seen.has(tool.slug)) {
        seen.add(tool.slug);
        const action = (s.label || '').toLowerCase();
        const verb = action.includes('send') ? 'sending' : action.includes('reply') ? 'replying' : 'automatic actions';
        appEntries.push({ ...tool, verb });
      }
    }
  }
  if (appEntries.length === 0) return null;
  return (
    <div className="mt-5 px-4 py-3 rounded-xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.08)]" data-testid="connections-panel">
      <p className="text-[10px] font-semibold text-[#7D8896] uppercase tracking-wider mb-2">Required connections</p>
      <div className="flex flex-col gap-1.5">
        {appEntries.map((t) => {
          const isConnected = connectedApps?.has(t.slug);
          const isConnecting = connectingApp === t.slug;
          return (
            <button key={t.slug || t.label}
              onClick={() => { if (!isConnected && !isConnecting) onConnectApp?.(t.label); }}
              disabled={isConnected || isConnecting}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs transition-colors ${
                isConnected
                  ? 'bg-[rgba(0,229,192,0.04)] border-[rgba(0,229,192,0.15)] text-[#00E5C0]'
                  : isConnecting
                  ? 'bg-[rgba(255,255,255,0.02)] border-[rgba(255,255,255,0.06)] text-[#7D8896] opacity-60'
                  : 'bg-[rgba(255,255,255,0.02)] border-[rgba(255,255,255,0.06)] text-[#B0B8C4] hover:border-[rgba(0,229,192,0.2)] hover:text-[#00E5C0]'
              }`}
              data-testid={`connect-panel-${(t.slug || t.label).toLowerCase().replace(/\s+/g, '-')}`}
            >
              {t.logo ? <img src={t.logo} alt={t.label} className="w-4 h-4 rounded object-contain" onError={(e) => { e.target.style.display = 'none'; }} /> : <ToolIcon tool={t} size={14} />}
              <span className="flex-1 text-left">
                {isConnected ? `${t.label} connected` : isConnecting ? `Connecting ${t.label}...` : `Connect ${t.label} to enable ${t.verb}`}
              </span>
              {isConnected ? <CheckCircle size={12} weight="fill" /> : isConnecting ? <CircleNotch size={12} className="animate-spin" /> : <PlugsConnected size={12} />}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────
   PIPELINE OVERVIEW BAR
   ───────────────────────────────────────────────── */
const PipelineOverview = ({ steps, workerType }) => {
  const isEvent = workerType === 'event';
  const inputs = steps.filter(s => s.type === 'trigger' || s.type === 'input');
  const processing = steps.filter(s => s.type === 'action' || s.type === 'condition');
  const controls = steps.filter(s => s.type === 'delay' || s.type === 'approval');
  const outputs = steps.filter(s => s.type === 'output');

  const sections = [
    { label: isEvent ? 'Triggers' : 'Inputs', count: inputs.length, color: isEvent ? '#00E5C0' : '#00E5C0', icon: isEvent ? Lightning : Upload },
    { label: 'Processing', count: processing.length, color: '#3B82F6', icon: Lightning },
    ...(controls.length > 0 ? [{ label: 'Controls', count: controls.length, color: '#8B5CF6', icon: Timer }] : []),
    { label: 'Outputs', count: outputs.length, color: '#10B981', icon: CheckCircle },
  ];

  return (
    <div className="flex items-center gap-1 px-4 py-3 bg-[rgba(255,255,255,0.02)] rounded-xl border border-[rgba(255,255,255,0.04)] mb-5" data-testid="pipeline-overview">
      {sections.map((sec, i) => {
        const Icon = sec.icon;
        return (
          <React.Fragment key={sec.label}>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg">
              <Icon size={13} style={{ color: sec.color }} weight="duotone" />
              <span className="text-[11px] text-[#B0B8C4] font-medium">{sec.label}</span>
              <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded-md" style={{ color: sec.color, backgroundColor: `${sec.color}10` }}>{sec.count}</span>
            </div>
            {i < sections.length - 1 && (
              <CaretRight size={10} className="text-[#2A3441] mx-0.5" />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

/* ─────────────────────────────────────────────────
   SECTION HEADER
   ───────────────────────────────────────────────── */
const SectionHeader = ({ title, color, icon: Icon, count }) => (
  <div className="flex items-center gap-2.5 mb-3 mt-1" data-testid={`section-header-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ backgroundColor: `${color}18`, border: `1px solid ${color}30` }}>
      <Icon size={13} style={{ color }} weight="duotone" />
    </div>
    <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color }}>{title}</span>
    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-md" style={{ color, backgroundColor: `${color}10` }}>{count}</span>
    <div className="flex-1 h-px" style={{ backgroundColor: `${color}18` }} />
  </div>
);

/* ─────────────────────────────────────────────────
   SECTION ADD BUTTON
   ───────────────────────────────────────────────── */
const SectionAddButton = ({ label, types, onAdd }) => {
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid={`add-${label.toLowerCase().replace(/\s/g, '-')}-btn`}
        className="w-full flex items-center justify-center gap-1.5 py-2 mt-2 mb-1 rounded-lg border border-dashed border-[rgba(255,255,255,0.08)] text-[#5C6778] hover:text-[#00E5C0] hover:border-[rgba(0,229,192,0.15)] transition-colors text-[11px]">
        <Plus size={10} weight="bold" /> Add {label}
      </button>
    );
  }

  return (
    <div className="flex flex-wrap gap-1.5 mt-2 mb-1 p-2 rounded-lg bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.08)]">
      {types.map(({ type, label: stepLabel, icon: Icon, color, stepName, extraConfig }) => (
        <button key={stepLabel} onClick={() => { onAdd({ type, label: stepName || `New ${type} step`, ...(extraConfig || {}) }); setOpen(false); }} data-testid={`section-add-${stepLabel.toLowerCase().replace(/[\s\/]+/g, '-')}`}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] text-[#B0B8C4] hover:text-[#F0F4F8] hover:bg-[rgba(255,255,255,0.04)] transition-colors">
          <Icon size={11} style={{ color }} /> {stepLabel}
        </button>
      ))}
      <button onClick={() => setOpen(false)} className="px-2 py-1 text-[10px] text-[#5C6778] hover:text-[#7D8896]">Cancel</button>
    </div>
  );
};

/* ─────────────────────────────────────────────────
   FLOW CONNECTOR (between sections)
   ───────────────────────────────────────────────── */
const FlowConnector = () => (
  <div className="flex justify-center py-4">
    <div className="flex flex-col items-center">
      <div className="w-0.5 h-4 rounded-full bg-[rgba(0,229,192,0.2)]" />
      <div className="w-6 h-6 rounded-full border border-[rgba(0,229,192,0.2)] bg-[rgba(0,229,192,0.04)] flex items-center justify-center">
        <CaretRight size={10} className="text-[#00E5C0] rotate-90" />
      </div>
      <div className="w-0.5 h-4 rounded-full bg-[rgba(0,229,192,0.2)]" />
    </div>
  </div>
);

/* ─────────────────────────────────────────────────
   FLOW PREVIEW — plain English summary
   ───────────────────────────────────────────────── */
const FlowPreview = ({ steps }) => {
  const [expanded, setExpanded] = useState(false);
  if (!steps || steps.length === 0) return null;

  const lines = steps.map((s) => {
    const label = (s.label || '').replace(/^(when |if |then |do )/i, '');
    switch (s.type) {
      case 'trigger':
      case 'input':
        return { verb: 'Detect', text: label.charAt(0).toLowerCase() + label.slice(1) };
      case 'condition': {
        const derived = _deriveBranchLabels(s.label);
        const yesLabel = s.yesBranch || derived.yes;
        const noLabel = s.noBranch || derived.no;
        return { verb: 'Check', text: `${label.charAt(0).toLowerCase() + label.slice(1)}`, sub: `${yesLabel} / ${noLabel}` };
      }
      case 'delay':
        return { verb: 'Wait', text: _delayLabel(s.delayConfig).replace(/^Wait /, '').toLowerCase() };
      case 'approval':
        return { verb: 'Pause', text: s.approvalNote ? `for approval — ${s.approvalNote}` : 'for manual approval' };
      case 'action':
        return { verb: 'Do', text: label.charAt(0).toLowerCase() + label.slice(1) };
      case 'output':
        return { verb: 'Then', text: label.charAt(0).toLowerCase() + label.slice(1) };
      default:
        return { verb: 'Step', text: label };
    }
  });

  return (
    <div className="mb-5 rounded-xl border border-[rgba(255,255,255,0.05)] bg-[rgba(255,255,255,0.015)] overflow-hidden" data-testid="flow-preview">
      <button onClick={() => setExpanded(e => !e)} className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-[rgba(255,255,255,0.02)] transition-colors" data-testid="flow-preview-toggle">
        <div className="flex items-center gap-2">
          <Play size={11} weight="fill" className="text-[#00E5C0]" />
          <span className="text-[11px] font-semibold text-[#B0B8C4]">Flow preview</span>
          <span className="text-[10px] text-[#5C6778]">— what this worker does</span>
        </div>
        <CaretRight size={10} className={`text-[#5C6778] transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`} />
      </button>
      {expanded && (
        <div className="px-4 pb-3.5 pt-0.5">
          <p className="text-[12px] text-[#7D8896] mb-2 font-medium">This worker will:</p>
          <ol className="space-y-1.5">
            {lines.map((line, i) => (
              <li key={i} className="flex items-start gap-2" data-testid={`flow-preview-line-${i}`}>
                <span className="text-[10px] font-bold text-[#5C6778] mt-0.5 w-4 text-right shrink-0">{i + 1}.</span>
                <div>
                  <span className="text-[12px]">
                    <span className="font-semibold text-[#B0B8C4]">{line.verb}</span>
                    <span className="text-[#7D8896]"> {line.text}</span>
                  </span>
                  {line.sub && <span className="text-[10px] text-[#5C6778] ml-1">({line.sub})</span>}
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────
   EXECUTION RESULT
   ───────────────────────────────────────────────── */
const ExecutionResult = ({ result, workerType, onClose }) => {
  if (!result) return null;
  const isFileWorker = workerType === 'file_analysis';
  return (
    <div className="mt-5 rounded-xl border border-[rgba(0,229,192,0.2)] bg-[rgba(0,229,192,0.02)] p-5" data-testid="execution-result">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><CheckCircle size={16} weight="fill" className="text-[#00E5C0]" /><span className="text-sm font-semibold text-[#F0F4F8]">Execution result</span></div>
        <div className="flex items-center gap-2">
          {isFileWorker && (
            <div className="flex items-center gap-1">
              {['JSON', 'CSV', 'PDF'].map(fmt => (
                <button key={fmt} data-testid={`export-${fmt.toLowerCase()}`}
                  className="px-2.5 py-1 rounded-md text-[10px] font-medium text-[#00E5C0] bg-[rgba(0,229,192,0.06)] border border-[rgba(0,229,192,0.12)] hover:bg-[rgba(0,229,192,0.1)] transition-colors">
                  {fmt}
                </button>
              ))}
            </div>
          )}
          <button onClick={onClose} className="text-[#7D8896] hover:text-[#F0F4F8] transition-colors"><X size={14} /></button>
        </div>
      </div>
      {result.summary && <p className="text-sm text-[#B0B8C4] mb-3">{result.summary}</p>}
      {result.execution_log && result.execution_log.length > 0 && (
        <div className="space-y-1.5 mb-3">
          {result.execution_log.map((log, i) => (
            <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[rgba(255,255,255,0.02)]" data-testid={`exec-log-${i}`}>
              {log.status === 'completed' ? (
                <CheckCircle size={12} weight="fill" className="text-[#00E5C0]" />
              ) : log.status === 'skipped' ? (
                <WarningCircle size={12} className="text-[#F59E0B]" />
              ) : (
                <WarningCircle size={12} className="text-[#FF6B6B]" />
              )}
              <span className="text-xs text-[#B0B8C4] flex-1">{log.label || `Step ${i + 1}`}</span>
              <span className={`text-[10px] font-medium ${log.status === 'completed' ? 'text-[#00E5C0]' : log.status === 'skipped' ? 'text-[#F59E0B]' : 'text-[#FF6B6B]'}`}>{log.status}</span>
            </div>
          ))}
        </div>
      )}
      {result.final_output && <div className="px-4 py-3 rounded-lg bg-[#1F2937] border border-[rgba(255,255,255,0.08)] text-sm text-[#F0F4F8] whitespace-pre-wrap">{result.final_output}</div>}
    </div>
  );
};

/* ─────────────────────────────────────────────────
   FILE WORKER CONFIG
   ───────────────────────────────────────────────── */

/* ─────────────────────────────────────────────────
   CHAT/VOICE/OPERATOR WORKER CONFIG
   ───────────────────────────────────────────────── */
const SimpleWorkerConfig = ({ workerType, config, onChange }) => {
  const titles = {
    chat: { label: 'Chat Worker', placeholder: 'e.g. You are a helpful customer support assistant for our plumbing business...' },
    voice: { label: 'Voice Worker', placeholder: 'e.g. Answer inbound calls professionally. Collect caller name, issue, and preferred time...' },
    operator: { label: 'Operator Worker', placeholder: 'e.g. Browse our competitor websites daily and compile a pricing comparison report...' },
  };
  const t = titles[workerType] || titles.chat;

  return (
    <div className="space-y-4" data-testid={`${workerType}-worker-config`}>
      <div>
        <p className="text-[11px] font-semibold text-[#7D8896] uppercase tracking-wider mb-2">Instructions</p>
        <textarea
          value={config?.instructions || ''}
          onChange={(e) => onChange({ ...config, instructions: e.target.value })}
          rows={4}
          data-testid={`${workerType}-instructions`}
          placeholder={t.placeholder}
          className="w-full px-4 py-3 bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-xl text-sm text-[#F0F4F8] placeholder-[#5C6778] focus:outline-none focus:border-[rgba(0,229,192,0.4)] transition-colors resize-none"
        />
      </div>
      {workerType === 'voice' && (
        <div>
          <p className="text-[11px] font-semibold text-[#7D8896] uppercase tracking-wider mb-2">Voice settings</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[#7D8896] mb-1 block">Language</label>
              <select value={config?.language || 'en'} onChange={(e) => onChange({ ...config, language: e.target.value })}
                className="w-full px-3 py-2 bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-lg text-sm text-[#F0F4F8] appearance-none">
                <option value="en">English</option><option value="es">Spanish</option><option value="fr">French</option><option value="de">German</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-[#7D8896] mb-1 block">Voice style</label>
              <select value={config?.voiceStyle || 'professional'} onChange={(e) => onChange({ ...config, voiceStyle: e.target.value })}
                className="w-full px-3 py-2 bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-lg text-sm text-[#F0F4F8] appearance-none">
                <option value="professional">Professional</option><option value="friendly">Friendly</option><option value="casual">Casual</option>
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ═════════════════════════════════════════════════
   SKILLS PANEL (Phase B: Feedback & Self-Learning)
   ═════════════════════════════════════════════════ */
const SKILL_CAT_META = {
  tone:        { label: 'Tone',        icon: ChatCircle,    color: '#8B5CF6', bg: 'rgba(139,92,246,0.08)' },
  rules:       { label: 'Rule',        icon: ShieldCheck,   color: '#F59E0B', bg: 'rgba(245,158,11,0.08)' },
  knowledge:   { label: 'Knowledge',   icon: Brain,         color: '#00E5C0', bg: 'rgba(0,229,192,0.08)' },
  preferences: { label: 'Preference',  icon: Lightning,     color: '#3B82F6', bg: 'rgba(59,130,246,0.08)' },
  constraints: { label: 'Constraint',  icon: WarningCircle, color: '#EF4444', bg: 'rgba(239,68,68,0.08)' },
};

const SkillsPanel = ({ skills, showSkillsPanel, setShowSkillsPanel, deleteSkill }) => {
  if (!skills?.length && !showSkillsPanel) return null;

  return (
    <div className="mt-4 bg-[#1E2836] border border-[rgba(255,255,255,0.08)] rounded-xl overflow-hidden" data-testid="skills-panel">
      <button
        onClick={() => setShowSkillsPanel(prev => !prev)}
        data-testid="skills-panel-toggle"
        className="w-full px-5 py-3.5 flex items-center justify-between border-b border-[rgba(255,255,255,0.04)] hover:bg-[rgba(255,255,255,0.02)] transition-colors"
      >
        <div className="flex items-center gap-2">
          <Brain size={15} className="text-[#8B5CF6]" />
          <span className="text-sm font-semibold text-[#F0F4F8]">Learned Skills</span>
          {skills.length > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[rgba(139,92,246,0.15)] text-[#8B5CF6] font-medium">{skills.length}</span>
          )}
        </div>
        <CaretRight size={14} className={`text-[#7D8896] transition-transform duration-200 ${showSkillsPanel ? 'rotate-90' : ''}`} />
      </button>

      {showSkillsPanel && (
        <div className="px-5 py-4 space-y-2">
          {skills.length === 0 ? (
            <p className="text-xs text-[#7D8896] text-center py-4">
              No skills yet. Teach your worker via Command — try "always mention our 24/7 service" or "be more formal in replies".
            </p>
          ) : (
            skills.map((skill) => {
              const cat = SKILL_CAT_META[skill.category] || SKILL_CAT_META.knowledge;
              const CatIcon = cat.icon;
              return (
                <div key={skill.id} data-testid={`skill-card-${skill.id}`}
                  className="group flex items-start gap-3 px-3 py-2.5 rounded-lg bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.04)] hover:border-[rgba(255,255,255,0.1)] transition-colors"
                >
                  <div className="w-6 h-6 rounded-md flex items-center justify-center shrink-0 mt-0.5" style={{ backgroundColor: cat.bg }}>
                    <CatIcon size={12} style={{ color: cat.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className="text-[9px] font-semibold uppercase tracking-wider" style={{ color: cat.color }}>{cat.label}</span>
                      {skill.source === 'command' && (
                        <span className="text-[8px] text-[#7D8896]">via Command</span>
                      )}
                    </div>
                    <p className="text-xs text-[#C8D1DB] leading-relaxed">{skill.instruction}</p>
                  </div>
                  <button
                    onClick={() => deleteSkill(skill.id)}
                    data-testid={`skill-delete-${skill.id}`}
                    className="opacity-0 group-hover:opacity-100 p-1 rounded text-[#7D8896] hover:text-[#F87171] hover:bg-[rgba(248,113,113,0.1)] transition-all"
                  >
                    <Trash size={12} />
                  </button>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};


/* ═════════════════════════════════════════════════
   MAIN BUILDER
   ═════════════════════════════════════════════════ */
export default function AutomationBuilder() {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeOrganization } = useOrganization();
  const { mode } = useMode();
  const orgId = activeOrganization?.organization_id;
  const isOperate = mode === 'operate';

  // Pre-load from worker detail "Edit workflow in builder"
  const editWorker = location.state?.editWorker;

  // Read type from URL query params (e.g. /workers/new?type=event)
  const searchParams = new URLSearchParams(location.search);
  const typeFromUrl = searchParams.get('type');

  // === State ===
  const [workerType, setWorkerType] = useState(editWorker ? (editWorker.worker_type || editWorker.automation_type || 'event') : typeFromUrl || null);
  const [typeLoading, setTypeLoading] = useState(!!typeFromUrl);
  const [eventTemplate, setEventTemplate] = useState(editWorker?.automation_type || null);
  const [workerName, setWorkerName] = useState(editWorker?.name || '');
  const [steps, setSteps] = useState(editWorker?.steps || []);
  const [workerConfig, setWorkerConfig] = useState(editWorker?.worker_config || editWorker?.config || {});
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(editWorker?.id || null);
  const [executing, setExecuting] = useState(false);
  const [executionResult, setExecutionResult] = useState(null);
  const [selectedStepIdx, setSelectedStepIdx] = useState(null);

  // Skills state (Phase B: Feedback & Self-Learning)
  const [skills, setSkills] = useState(editWorker?.skills || []);
  const [showSkillsPanel, setShowSkillsPanel] = useState(false);

  // Wizard modals for Voice and Operator
  const [showVoiceWizard, setShowVoiceWizard] = useState(false);
  const [voiceWizardAgentId, setVoiceWizardAgentId] = useState(null);
  const [showOperatorModal, setShowOperatorModal] = useState(false);

  // Composio connection state
  const [connectedApps, setConnectedApps] = useState(new Set());
  const [connectingApp, setConnectingApp] = useState(null);

  // Fetch Composio connection status
  useEffect(() => {
    if (!orgId) return;
    checkConnectionStatus(orgId).then(data => {
      setConnectedApps(new Set(data.connected_apps || []));
    }).catch(() => {});
  }, [orgId]);

  // Handle Composio OAuth connection for an app
  const handleConnectApp = async (appName) => {
    const slug = getSlug(appName);
    setConnectingApp(slug);
    try {
      const callbackUrl = `${window.location.origin}/oauth/callback?connected=${slug}`;
      const data = await connectApp(appName, orgId, callbackUrl);
      if (data.redirect_url) {
        toast.info(`Opening ${appName} authorization...`);
        await openOAuthPopup(data.redirect_url);
        // Re-check connections after popup closes
        await new Promise(r => setTimeout(r, 1500));
        const freshData = await checkConnectionStatus(orgId);
        const freshSet = new Set(freshData.connected_apps || []);
        setConnectedApps(freshSet);
        if (freshSet.has(slug)) {
          toast.success(`${appName} connected!`);
        } else {
          await new Promise(r => setTimeout(r, 2000));
          const retryData = await checkConnectionStatus(orgId);
          setConnectedApps(new Set(retryData.connected_apps || []));
          toast.info(`Authorization completed. ${appName} may take a moment to activate.`);
        }
      }
    } catch (err) {
      toast.error(err.message || `Failed to connect ${appName}`);
    } finally {
      setConnectingApp(null);
    }
  };

  const selectWorkerType = async (typeId) => {
    if (typeId === 'voice') {
      // Create draft voice agent, then open wizard modal
      try {
        const response = await fetchWithOrg(
          `${BACKEND_URL}/api/agents`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: 'New Voice Agent',
              short_description: 'Voice agent for phone-based interactions',
              capability_profile: 'live_voice_s2s_agent',
              accepted_inputs: ['audio', 'text'],
              status: 'draft',
              is_active: false,
              org_id: orgId
            })
          },
          orgId
        );
        if (response.ok) {
          const data = await response.json();
          const id = data.agent_id || data.agent?.id || data.id;
          setVoiceWizardAgentId(id);
          setShowVoiceWizard(true);
        } else {
          toast.error('Failed to create voice agent');
        }
      } catch (error) {
        toast.error('Failed to create voice agent');
      }
      return;
    }

    if (typeId === 'operator') {
      setShowOperatorModal(true);
      return;
    }

    setWorkerType(typeId);
    if (typeId !== 'event') {
      setSteps((INITIAL_STEPS[typeId] || []).map(s => ({ ...s })));
      setWorkerName('');
      setSavedId(null);
      setExecutionResult(null);
      setEventTemplate(typeId);
    }
  };

  const selectEventTemplate = (templateId) => {
    setEventTemplate(templateId);
    setSteps((INITIAL_STEPS[templateId] || []).map(s => ({ ...s })));
    setWorkerName('');
    setSavedId(null);
    setExecutionResult(null);
  };

  // Auto-select worker type from URL query param (from modal navigation)
  useEffect(() => {
    if (typeFromUrl && !editWorker) {
      selectWorkerType(typeFromUrl).finally(() => setTypeLoading(false));
    } else {
      setTypeLoading(false);
    }
  }, []); // eslint-disable-line

  const updateStep = (idx, updatedStep) => setSteps(prev => prev.map((s, i) => i === idx ? updatedStep : s));
  const deleteStep = (idx) => { setSteps(prev => prev.filter((_, i) => i !== idx)); if (selectedStepIdx === idx) setSelectedStepIdx(null); };
  const addStep = (step) => setSteps(prev => [...prev, step]);

  // Drag-to-reorder within sections (constrained)
  const dragSrc = useRef(null);
  const [dragTarget, setDragTarget] = useState(null);
  const handleDragStart = (globalIdx) => { dragSrc.current = globalIdx; };
  const handleDragOver = (globalIdx) => { if (dragSrc.current !== null && dragSrc.current !== globalIdx) setDragTarget(globalIdx); };
  const handleDrop = (globalIdx) => {
    const src = dragSrc.current;
    if (src === null || src === globalIdx) { dragSrc.current = null; setDragTarget(null); return; }
    // Only allow reorder within the same section type group
    const srcType = steps[src]?.type;
    const dstType = steps[globalIdx]?.type;
    const sameSection = (t1, t2) => {
      const inputTypes = ['trigger', 'input'];
      const procTypes = ['action', 'condition', 'delay', 'approval'];
      const outTypes = ['output'];
      if (inputTypes.includes(t1) && inputTypes.includes(t2)) return true;
      if (procTypes.includes(t1) && procTypes.includes(t2)) return true;
      if (outTypes.includes(t1) && outTypes.includes(t2)) return true;
      return false;
    };
    if (!sameSection(srcType, dstType)) { dragSrc.current = null; setDragTarget(null); return; }
    setSteps(prev => {
      const next = [...prev];
      const [moved] = next.splice(src, 1);
      const insertAt = src < globalIdx ? globalIdx - 1 : globalIdx;
      next.splice(insertAt, 0, moved);
      return next;
    });
    dragSrc.current = null;
    setDragTarget(null);
  };

  // Track which step indices were just added by Command (for highlight animation)
  const [highlightedStepIndices, setHighlightedStepIndices] = useState([]);

  // Auto-open Command with workflow context.
  // Dispatch only when the user has progressed past the template-picker stage:
  //   • Non-event types (file/voice/etc.) — immediately on mount
  //   • Event type — only after a template (incl. 'custom') is selected
  // This prevents Command from opening prematurely on the template-chooser page.
  useEffect(() => {
    const isReady = (workerType && workerType !== 'event') || !!eventTemplate;
    if (!isReady) {
      // On the picker stage, close Command if it's open from a prior dispatch
      // and clear any stale workflowContext / auto-binding so the visible UI
      // (template cards) is the only thing the user sees.
      window.dispatchEvent(new CustomEvent('close-command-drawer'));
      return;
    }
    const wType = WORKER_TYPES.find(t => t.id === workerType);
    window.dispatchEvent(new CustomEvent('open-command-drawer', {
      detail: {
        context: 'workflow',
        workerName: workerName || editWorker?.name || wType?.label || EVENT_TEMPLATES.find(t => t.id === eventTemplate)?.label || 'New Worker',
        workerId: savedId || editWorker?.id,
        automationType: eventTemplate || workerType,
        workerType: workerType,
        steps,
      }
    }));
  }, [workerType, eventTemplate]);

  // Keep Command in sync with current steps (for Command → Builder binding).
  // Same gating as above — only sync when we're past the picker.
  useEffect(() => {
    const isReady = (workerType && workerType !== 'event') || !!eventTemplate;
    if (!isReady) return;
    const wType = WORKER_TYPES.find(t => t.id === workerType);
    window.dispatchEvent(new CustomEvent('builder-steps-sync', {
      detail: {
        steps,
        workerType: workerType,
        workerName: workerName || editWorker?.name || wType?.label || '',
        workerId: savedId || editWorker?.id,
        skills,
      }
    }));
  }, [steps, workerType, workerName, skills]);

  // Listen for step updates from Command pane (Phase A: real-time binding)
  useEffect(() => {
    const handleStepUpdate = (e) => {
      const { updatedSteps, operations } = e.detail || {};
      if (updatedSteps && Array.isArray(updatedSteps)) {
        setSteps(updatedSteps);
        // Highlight newly added steps
        const addedIndices = (operations || [])
          .filter(op => op.op === 'add')
          .map(op => op.index);
        if (addedIndices.length > 0) {
          setHighlightedStepIndices(addedIndices);
          setTimeout(() => setHighlightedStepIndices([]), 2500);
        }
        toast.success('Step updated via Command');
      }
    };
    window.addEventListener('builder-step-update', handleStepUpdate);
    return () => window.removeEventListener('builder-step-update', handleStepUpdate);
  }, []);

  // Listen for skill updates from Command pane (Phase B: Feedback & Self-Learning)
  useEffect(() => {
    const handleSkillUpdate = (e) => {
      const { skill, action } = e.detail || {};
      if (skill && action === 'add') {
        setSkills(prev => [...prev, skill]);
        setShowSkillsPanel(true);
        toast.success('Skill learned');
      } else if (skill && action === 'remove') {
        setSkills(prev => prev.filter(s => s.id !== skill.id));
      }
    };
    window.addEventListener('builder-skill-update', handleSkillUpdate);
    return () => window.removeEventListener('builder-skill-update', handleSkillUpdate);
  }, []);

  const handleSave = async () => {
    if (!steps.length) return;
    setSaving(true);
    try {
      const name = workerName || WORKER_TYPES.find(t => t.id === workerType)?.label || EVENT_TEMPLATES.find(t => t.id === eventTemplate)?.label || 'Worker';
      const method = savedId ? 'PATCH' : 'POST';
      const url = savedId
        ? `${BACKEND_URL}/api/service-os/automations/${savedId}`
        : `${BACKEND_URL}/api/service-os/automations`;
      const body = savedId
        ? { steps, skills, status: 'active', name: workerName || undefined, worker_config: workerConfig }
        : { name, automation_type: eventTemplate || workerType, steps, skills, status: 'active' };

      // Include worker_type and config for non-event workers
      if (workerType !== 'event') {
        body.worker_type = workerType;
      }
      // Always include worker_config (design-time settings for file/analysis workers etc.)
      if (Object.keys(workerConfig).length > 0) {
        body.worker_config = workerConfig;
      }

      const res = await fetchWithOrg(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }, orgId);
      const data = await res.json();
      if (data.automation?.id) {
        setSavedId(data.automation.id);
        toast.success('Worker saved');
        // Close Command and navigate back to worker detail
        window.dispatchEvent(new CustomEvent('close-command-drawer'));
        navigate(`/workers/${data.automation.id}/detail`);
      } else {
        toast.error('Failed to save');
      }
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const deleteSkill = async (skillId) => {
    setSkills(prev => prev.filter(s => s.id !== skillId));
    // If saved, also delete from DB
    if (savedId) {
      try {
        await fetchWithOrg(
          `${BACKEND_URL}/api/service-os/automations/${savedId}/skills/${skillId}`,
          { method: 'DELETE' },
          orgId
        );
      } catch { /* silent — already removed from local state */ }
    }
    toast.success('Skill removed');
  };

  const handleExecute = async () => {
    if (!savedId) { toast.info('Save the worker first'); return; }
    setExecuting(true);
    setExecutionResult(null);
    try {
      const res = await fetchWithOrg(
        `${BACKEND_URL}/api/service-os/automations/${savedId}/execute`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            test_input: {
              enquiry: 'Hi, I need a plumber for a leaking tap. Are you available this week?',
              customer_name: 'Sarah',
              is_after_hours: eventTemplate === 'after_hours',
            },
          }),
        },
        orgId
      );
      const data = await res.json();
      setExecutionResult(data);
      toast.success('Worker tested successfully');
    } catch { toast.error('Execution failed'); }
    finally { setExecuting(false); }
  };

  // Validation
  const hasInput = steps.some(s => s.type === 'trigger' || s.type === 'input');
  const hasOutput = steps.some(s => s.type === 'output' || s.type === 'action');
  const validationWarning = steps.length > 0 && (!hasInput || !hasOutput) ? 'This worker needs at least one input and one output to produce a usable outcome.' : null;

  /* Wizard modals rendered at all screen levels */
  const wizardModals = (
    <>
      {/* Voice Wizard Modal */}
      {showVoiceWizard && voiceWizardAgentId && (
        <div data-testid="voice-wizard-modal-wrapper">
          <UniversalVoiceWizard
            agentIdProp={voiceWizardAgentId}
            isModal={true}
            onClose={(agentId) => {
              setShowVoiceWizard(false);
              setVoiceWizardAgentId(null);
              if (agentId) {
                navigate(`/partner-pro/ai-workforce/agents/${agentId}`);
              }
            }}
          />
        </div>
      )}

      {/* Operator Create Modal */}
      <CreateOperatorModal
        isOpen={showOperatorModal}
        onClose={() => setShowOperatorModal(false)}
        onSuccess={(newOperator) => {
          setShowOperatorModal(false);
          if (newOperator?.id) {
            navigate(`/operators/${newOperator.id}`);
          }
        }}
        orgId={orgId}
      />
    </>
  );

  /* ── SCREEN 1: WORKER TYPE SELECTION ── */
  if (typeLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <CircleNotch size={32} className="animate-spin text-[#00E5C0] mx-auto mb-3" />
          <p className="text-sm text-[#7D8896]">Setting up your worker...</p>
        </div>
      </div>
    );
  }

  if (!workerType) {
    return (
      <>
        <SEO title="New Worker" />
        <div className="max-w-[800px] mx-auto" data-testid="worker-type-selector">
          <div className="mb-8">
            <h1 className="text-2xl font-semibold text-[#F0F4F8]">Create a new worker</h1>
            <p className="text-sm text-[#7D8896] mt-1">Choose the type of worker you want to build.</p>
          </div>
          <div className="space-y-3">
            {/* Guided Setup Wizard — top option */}
            <button onClick={() => navigate('/workers/setup')} data-testid="worker-type-guided-setup"
              className="w-full flex items-center gap-5 bg-[#1E2836] border border-[rgba(0,229,192,0.15)] rounded-xl p-5 text-left hover:border-[rgba(0,229,192,0.3)] transition-all group">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 bg-[rgba(0,229,192,0.1)]">
                <MagicWand size={24} className="text-[#00E5C0]" weight="duotone" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-medium text-[#F0F4F8] group-hover:text-[#00E5C0] transition-colors">Easy Template Setup Wizard</h3>
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-[rgba(0,229,192,0.08)] text-[#00E5C0] font-semibold uppercase tracking-wider">Recommended</span>
                </div>
                <p className="text-xs text-[#7D8896] mt-0.5">Step-by-step guided setup — choose tools, and configure in minutes.</p>
              </div>
              <CaretRight size={16} className="text-[#5C6778] group-hover:text-[#00E5C0] transition-colors shrink-0" />
            </button>

            {/* Standard worker types */}
            {WORKER_TYPES.filter(wt => !['voice', 'operator'].includes(wt.id)).map((wt) => {
              const Icon = wt.icon;
              return (
                <button key={wt.id} onClick={() => selectWorkerType(wt.id)} data-testid={`worker-type-${wt.id}`}
                  className="w-full flex items-center gap-5 bg-[#1E2836] border border-[rgba(255,255,255,0.08)] rounded-xl p-5 text-left hover:border-[rgba(0,229,192,0.2)] transition-all group">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${wt.color}12` }}>
                    <Icon size={24} style={{ color: wt.color }} weight="duotone" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-medium text-[#F0F4F8] group-hover:text-[#00E5C0] transition-colors">{wt.label}</h3>
                      {wt.tag && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-[rgba(0,229,192,0.08)] text-[#00E5C0] font-semibold uppercase tracking-wider">{wt.tag}</span>
                      )}
                    </div>
                    <p className="text-xs text-[#7D8896] mt-0.5">{wt.description}</p>
                  </div>
                  <CaretRight size={16} className="text-[#5C6778] group-hover:text-[#00E5C0] transition-colors shrink-0" />
                </button>
              );
            })}

            {/* Divider — Specialty Workers */}
            <div className="flex items-center gap-3 pt-4 pb-1">
              <div className="flex-1 h-px bg-[rgba(255,255,255,0.06)]" />
              <span className="text-[10px] text-[#5C6778] uppercase tracking-widest font-medium">Specialty Workers</span>
              <div className="flex-1 h-px bg-[rgba(255,255,255,0.06)]" />
            </div>

            {/* Specialty worker types (Voice, Operator) */}
            {WORKER_TYPES.filter(wt => ['voice', 'operator'].includes(wt.id)).map((wt) => {
              const Icon = wt.icon;
              return (
                <button key={wt.id} onClick={() => selectWorkerType(wt.id)} data-testid={`worker-type-${wt.id}`}
                  className="w-full flex items-center gap-5 bg-[#1E2836] border border-[rgba(255,255,255,0.08)] rounded-xl p-5 text-left hover:border-[rgba(0,229,192,0.2)] transition-all group">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${wt.color}12` }}>
                    <Icon size={24} style={{ color: wt.color }} weight="duotone" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-medium text-[#F0F4F8] group-hover:text-[#00E5C0] transition-colors">{wt.label}</h3>
                      {wt.tag && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-[rgba(0,229,192,0.08)] text-[#00E5C0] font-semibold uppercase tracking-wider">{wt.tag}</span>
                      )}
                    </div>
                    <p className="text-xs text-[#7D8896] mt-0.5">{wt.description}</p>
                  </div>
                  <CaretRight size={16} className="text-[#5C6778] group-hover:text-[#00E5C0] transition-colors shrink-0" />
                </button>
              );
            })}
          </div>
        </div>
        {wizardModals}
      </>
    );
  }

  /* ── SCREEN 2 (Event only): EVENT TEMPLATE SELECTION ── */
  if (workerType === 'event' && !eventTemplate) {
    return (
      <>
        <SEO title="Event Worker" />
        <div className="max-w-[800px] mx-auto" data-testid="event-template-selector">
          <div className="flex items-center gap-3 mb-8">
            <button onClick={() => setWorkerType(null)} data-testid="back-to-types" className="p-2 rounded-lg border border-[rgba(255,255,255,0.08)] text-[#B0B8C4] hover:text-[#F0F4F8] hover:bg-[rgba(255,255,255,0.04)] transition-colors">
              <ArrowLeft size={16} />
            </button>
            <div>
              <h1 className="text-2xl font-semibold text-[#F0F4F8]">Event Worker</h1>
              <p className="text-sm text-[#7D8896] mt-0.5">Choose a starting template or build from scratch.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {EVENT_TEMPLATES.map((tmpl) => {
              const Icon = tmpl.icon;
              return (
                <button key={tmpl.id} onClick={() => selectEventTemplate(tmpl.id)} data-testid={`event-template-${tmpl.id}`}
                  className="bg-[#1E2836] border border-[rgba(255,255,255,0.08)] rounded-xl p-5 text-left hover:border-[rgba(0,229,192,0.2)] transition-all group">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: `${tmpl.color}15` }}>
                    <Icon size={20} style={{ color: tmpl.color }} />
                  </div>
                  <h3 className="text-sm font-medium text-[#F0F4F8] mb-0.5 group-hover:text-[#00E5C0] transition-colors">{tmpl.label}</h3>
                  <p className="text-xs text-[#7D8896]">{tmpl.description}</p>
                </button>
              );
            })}
          </div>
        </div>
      </>
    );
  }

  /* ── SCREEN 3: WORKER BUILDER ── */
  const activeType = WORKER_TYPES.find(t => t.id === workerType);
  const TypeIcon = activeType?.icon || Lightning;

  return (
    <>
      <SEO title={workerName || activeType?.label || 'New Worker'} />
      <div className="max-w-[960px] mx-auto" data-testid="worker-builder">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button onClick={() => {
              window.dispatchEvent(new CustomEvent('close-command-drawer'));
              if (editWorker) { navigate(-1); }
              else if (workerType === 'event') { setEventTemplate(null); setSteps([]); setSelectedStepIdx(null); }
              else { setWorkerType(null); setSteps([]); setSelectedStepIdx(null); }
            }} data-testid="builder-back-btn" className="p-2 rounded-lg border border-[rgba(255,255,255,0.08)] text-[#B0B8C4] hover:text-[#F0F4F8] hover:bg-[rgba(255,255,255,0.04)] transition-colors">
              <ArrowLeft size={16} />
            </button>
            <div className="flex items-center gap-2">
              <img
                src={getWorkerIcon(editWorker?.outcome_category, workerName || editWorker?.name)}
                alt=""
                className="w-8 h-8 rounded-lg object-cover"
                data-testid="builder-worker-icon"
              />
              <div>
                <input
                  value={workerName}
                  onChange={(e) => setWorkerName(e.target.value)}
                  placeholder={editWorker?.name || activeType?.label || 'Worker name'}
                  data-testid="worker-name-input"
                  className="text-lg font-semibold text-[#F0F4F8] bg-transparent border-none outline-none placeholder-[#5C6778] w-full"
                />
                <p className="text-[10px] text-[#7D8896] uppercase tracking-wider">{activeType?.label}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={handleExecute} disabled={executing || !savedId} data-testid="test-worker-btn"
              className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg border border-[rgba(255,255,255,0.08)] text-[#F0F4F8] hover:bg-[rgba(255,255,255,0.04)] transition-colors disabled:opacity-40">
              {executing ? <CircleNotch size={14} className="animate-spin" /> : <Play size={14} />} Test
            </button>
            <button onClick={handleSave} disabled={saving} data-testid="save-worker-btn"
              className="flex items-center gap-2 px-5 py-2.5 bg-[#00E5C0] text-[#12161C] text-sm font-medium rounded-lg hover:bg-[#00CCB0] transition-colors disabled:opacity-50">
              {saving ? <CircleNotch size={14} className="animate-spin" /> : <Check size={14} weight="bold" />} {savedId ? 'Update' : 'Save'}
            </button>
          </div>
        </div>

        {/* Type-specific config — File, Chat, Voice, Operator */}
        {workerType === 'file_analysis' && (
          <div className="mb-6 bg-[#1E2836] border border-[rgba(255,255,255,0.08)] rounded-xl p-5">
            <FileWorkerConfig config={workerConfig} onChange={setWorkerConfig} agentId={savedId} />
          </div>
        )}
        {(workerType === 'chat' || workerType === 'voice' || workerType === 'operator') && (
          <div className="mb-6 bg-[#1E2836] border border-[rgba(255,255,255,0.08)] rounded-xl p-5">
            <SimpleWorkerConfig workerType={workerType} config={workerConfig} onChange={setWorkerConfig} />
          </div>
        )}

        {/* Validation warning */}
        {validationWarning && (
          <div className="mb-4 px-4 py-3 rounded-xl bg-[rgba(245,158,11,0.06)] border border-[rgba(245,158,11,0.15)] flex items-center gap-2" data-testid="validation-warning">
            <WarningCircle size={16} className="text-[#F59E0B] shrink-0" />
            <p className="text-xs text-[#F59E0B]">{validationWarning}</p>
          </div>
        )}

        {/* Pipeline + Detail Panel */}
        <div className="flex gap-5">
          <div className={`${selectedStepIdx !== null ? 'flex-1 min-w-0' : 'w-full'} bg-[#1E2836] border border-[rgba(255,255,255,0.08)] rounded-xl overflow-hidden transition-all duration-200`}>
            <div className="px-5 py-4 border-b border-[rgba(255,255,255,0.08)] flex items-center justify-between">
              <div>
                <span className="text-sm font-semibold text-[#F0F4F8]">Pipeline</span>
                <span className="text-xs text-[#7D8896] ml-2">{steps.length} step{steps.length !== 1 ? 's' : ''}</span>
              </div>
            </div>
            <div className="px-5 py-5">
              {steps.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-sm text-[#7D8896]">No steps yet. Add steps below or use Command.</p>
                </div>
              ) : (() => {
                _seenProviders.current = new Set(); // Reset provider tracking per render
                const isEvent = workerType === 'event';
                const inputLabel = isEvent ? 'Triggers' : 'Inputs';
                const inputColor = isEvent ? '#00E5C0' : '#00E5C0';
                const InputIcon = isEvent ? Lightning : Upload;

                const indexed = steps.map((s, i) => ({ step: s, globalIndex: i }));
                const inputSteps = indexed.filter(({ step }) => step.type === 'trigger' || step.type === 'input');
                const processingSteps = indexed.filter(({ step }) => step.type === 'action' || step.type === 'condition' || step.type === 'delay' || step.type === 'approval');
                const outputSteps = indexed.filter(({ step }) => step.type === 'output');

                return (
                  <>
                    <PipelineOverview steps={steps} workerType={workerType} />
                    <FlowPreview steps={steps} />

                    {/* INPUTS SECTION */}
                    <div className="rounded-xl border border-[rgba(255,255,255,0.05)] bg-[rgba(255,255,255,0.01)] p-4 mb-0" data-testid="section-inputs-container">
                    <SectionHeader title={inputLabel} color={inputColor} icon={InputIcon} count={inputSteps.length} />
                    {inputSteps.map(({ step, globalIndex }, secIdx) => (
                      <StepCard key={globalIndex} step={step} index={globalIndex} sectionIndex={secIdx} allSteps={steps} onDelete={deleteStep} onSelect={setSelectedStepIdx} isSelected={selectedStepIdx === globalIndex} executionLog={executionResult?.execution_log} isHighlighted={highlightedStepIndices.includes(globalIndex)} connectedApps={connectedApps} connectingApp={connectingApp} onConnectApp={handleConnectApp} onDragStart={handleDragStart} onDragOver={handleDragOver} onDrop={handleDrop} isDragTarget={dragTarget === globalIndex} />
                    ))}
                    <SectionAddButton label={isEvent ? 'trigger' : 'input'} types={isEvent
                      ? [{ type: 'trigger', label: 'Trigger', icon: Lightning, color: '#00E5C0' }]
                      : [{ type: 'input', label: 'Input source', icon: Upload, color: inputColor }]
                    } onAdd={addStep} />
                    </div>

                    <FlowConnector />

                    {/* PROCESSING SECTION */}
                    <div className="rounded-xl border border-[rgba(255,255,255,0.05)] bg-[rgba(255,255,255,0.01)] p-4 mb-0" data-testid="section-processing-container">
                    <SectionHeader title="Processing" color="#3B82F6" icon={Lightning} count={processingSteps.length} />
                    {processingSteps.map(({ step, globalIndex }, secIdx) => (
                      <StepCard key={globalIndex} step={step} index={globalIndex} sectionIndex={secIdx} allSteps={steps} onDelete={deleteStep} onSelect={setSelectedStepIdx} isSelected={selectedStepIdx === globalIndex} executionLog={executionResult?.execution_log} isHighlighted={highlightedStepIndices.includes(globalIndex)} connectedApps={connectedApps} connectingApp={connectingApp} onConnectApp={handleConnectApp} onDragStart={handleDragStart} onDragOver={handleDragOver} onDrop={handleDrop} isDragTarget={dragTarget === globalIndex} />
                    ))}
                    <SectionAddButton label="processing step" types={[
                      { type: 'action', label: 'Action', icon: Lightning, color: '#3B82F6' },
                      { type: 'condition', label: 'Condition', icon: TreeStructure, color: '#F59E0B', stepName: 'If enquiry is urgent', extraConfig: { yesBranch: 'Urgent — prioritise', noBranch: 'Not urgent — continue normally' } },
                      { type: 'delay', label: 'Delay', icon: Timer, color: '#8B5CF6', stepName: 'Wait 30 minutes', extraConfig: { delayConfig: { type: 'duration', value: 30, unit: 'minutes' } } },
                      { type: 'approval', label: 'Approval', icon: HandPalm, color: '#EF4444', stepName: 'Wait for approval before continuing' },
                    ]} onAdd={addStep} />
                    </div>

                    <FlowConnector />

                    {/* OUTPUTS SECTION */}
                    <div className="rounded-xl border border-[rgba(255,255,255,0.05)] bg-[rgba(255,255,255,0.01)] p-4 mb-0" data-testid="section-outputs-container">
                    <SectionHeader title="Outputs" color="#10B981" icon={CheckCircle} count={outputSteps.length} />
                    {outputSteps.map(({ step, globalIndex }, secIdx) => (
                      <StepCard key={globalIndex} step={step} index={globalIndex} sectionIndex={secIdx} allSteps={steps} onDelete={deleteStep} onSelect={setSelectedStepIdx} isSelected={selectedStepIdx === globalIndex} executionLog={executionResult?.execution_log} isHighlighted={highlightedStepIndices.includes(globalIndex)} connectedApps={connectedApps} connectingApp={connectingApp} onConnectApp={handleConnectApp} onDragStart={handleDragStart} onDragOver={handleDragOver} onDrop={handleDrop} isDragTarget={dragTarget === globalIndex} />
                    ))}
                    <SectionAddButton label="output" types={workerType === 'file_analysis'
                      ? [
                          { type: 'output', label: 'Activity Feed', icon: CheckCircle, color: '#10B981', stepName: 'Save to Activity Feed' },
                          { type: 'output', label: 'PDF Report', icon: FilePdf, color: '#FF6B6B', stepName: 'Export as PDF report' },
                          { type: 'output', label: 'CSV / Excel', icon: FileXls, color: '#3B82F6', stepName: 'Export as CSV / Excel spreadsheet' },
                          { type: 'output', label: 'JSON Export', icon: FileText, color: '#F59E0B', stepName: 'Export as JSON data' },
                          { type: 'output', label: 'Email Report', icon: EnvelopeSimple, color: '#8B5CF6', stepName: 'Send results via email' },
                        ]
                      : [
                          { type: 'output', label: 'Output destination', icon: CheckCircle, color: '#10B981' },
                        ]
                    } onAdd={addStep} />
                    </div>
                  </>
                );
              })()}
              <ConnectionsPanel steps={steps} connectedApps={connectedApps} connectingApp={connectingApp} onConnectApp={handleConnectApp} />
              <ExecutionResult result={executionResult} workerType={workerType} onClose={() => setExecutionResult(null)} />
            </div>
          </div>

          {selectedStepIdx !== null && steps[selectedStepIdx] && (
            <div className="w-[380px] flex-shrink-0 transition-all duration-200" data-testid="step-detail-container">
              <StepDetailPanel step={steps[selectedStepIdx]} index={selectedStepIdx} workerType={workerType} onUpdate={updateStep} onClose={() => setSelectedStepIdx(null)} connectedApps={connectedApps} connectingApp={connectingApp} onConnectApp={handleConnectApp} allSteps={steps} />
            </div>
          )}
        </div>

        {/* Skills Panel (Phase B: Feedback & Self-Learning) */}
        <SkillsPanel skills={skills} showSkillsPanel={showSkillsPanel} setShowSkillsPanel={setShowSkillsPanel} deleteSkill={deleteSkill} />
      </div>

      {wizardModals}
    </>
  );
}
